/**
 * QR SCANNER COMPONENT
 */

import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

export default function QRScanner({ onScanSuccess, onClose }) {
    const [error, setError] = useState('');
    const [isStarted, setIsStarted] = useState(false);
    const html5QrCodeRef = useRef(null);

    const stopScanner = async () => {
        if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
            try {
                await html5QrCodeRef.current.stop();
            } catch (e) {}
        }
    };

    const startScanner = async () => {
        setError('');
        setIsStarted(false);
        await stopScanner();

        // Give the DOM a moment to ensure 'reader' element is ready
        setTimeout(async () => {
            try {
                const html5QrCode = new Html5Qrcode("reader");
                html5QrCodeRef.current = html5QrCode;

                await html5QrCode.start(
                    { facingMode: "environment" }, 
                    { fps: 20, qrbox: { width: 250, height: 250 } },
                    (decodedText) => {
                        html5QrCode.stop().then(() => onScanSuccess(decodedText)).catch(() => onScanSuccess(decodedText));
                    },
                    (errorMessage) => {
                        // ignore constant scanning errors
                    }
                );
                setIsStarted(true);
            } catch (err) {
                console.error("Camera error:", err);
                const errStr = err.toString();
                if (errStr.includes('NotFound') || errStr.includes('NotAllowed')) {
                    setError("Camera permission denied or camera not found.");
                } else if (errStr.includes('AbortError') || errStr.includes('In Use')) {
                    setError("Camera is busy. Close other apps and click 'Retry'.");
                } else {
                    // Fallback to user camera
                    try {
                        const h = new Html5Qrcode("reader");
                        html5QrCodeRef.current = h;
                        await h.start({ facingMode: "user" }, { fps: 20 }, (text) => {
                            h.stop().then(() => onScanSuccess(text)).catch(() => onScanSuccess(text));
                        }, () => {});
                        setIsStarted(true);
                    } catch (e2) {
                        setError("Could not access camera. Please check permissions or use 'Upload Image'.");
                    }
                }
            }
        }, 300);
    };

    useEffect(() => {
        // Run once on mount
        startScanner();
        
        return () => {
            stopScanner();
        };
    }, []);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const html5QrCode = new Html5Qrcode("reader");
        html5QrCode.scanFile(file, true)
            .then(decodedText => onScanSuccess(decodedText))
            .catch(() => setError("No QR code found in that image. Try again."));
    };

    const handleManualScan = () => {
        const token = window.prompt("Enter exact QR Token for testing:");
        if (token) onScanSuccess(token);
    };

    return (
        <div style={styles.container}>
            <style>{`
                #reader video {
                    object-fit: cover !important;
                    width: 100% !important;
                    height: 100% !important;
                }
            `}</style>
            <div style={styles.header}>
                <div style={styles.title}>Scan QR Code</div>
                <button style={styles.closeBtn} onClick={onClose}>✕</button>
            </div>

            <div style={styles.scannerViewport}>
                {!isStarted && !error && <div style={styles.loadingText}>Initializing camera...</div>}
                <div id="reader" style={styles.readerDiv}></div>
                <div style={styles.scannerAnimation} />
            </div>

            <div style={styles.controls}>
                {error && (
                    <div style={styles.errorBox}>
                        <div style={styles.errorMsg}>⚠️ {error}</div>
                        <button style={styles.retryBtn} onClick={startScanner}>🔄 Retry Camera</button>
                    </div>
                )}
                
                <label style={styles.fileLabel}>
                    📁 Scan Image File (Guaranteed)
                    <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                </label>

                <button style={styles.manualBtn} onClick={handleManualScan}>
                    ⌨️ Manual Entry (Token)
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: { 
        flex: 1, display: 'flex', flexDirection: 'column', padding: 20, 
        maxWidth: 500, margin: '0 auto', width: '100%',
        color: '#fff'
    },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
    title: { color: '#fff', fontSize: 20, fontWeight: 'bold', fontFamily: 'Georgia, serif' },
    closeBtn: { background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', padding: 5, opacity: 0.7 },
    scannerViewport: { 
        position: 'relative', 
        width: '100%',
        aspectRatio: '1/1',
        background: '#000', 
        borderRadius: 24, 
        overflow: 'hidden', 
        border: '2px solid #B87333', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
    },
    readerDiv: { 
        width: '100%', 
        height: '100%', 
        position: 'absolute',
        '& video': {
            objectFit: 'cover !important',
            width: '100% !important',
            height: '100% !important'
        }
    },
    loadingText: { color: '#fff', opacity: 0.5, zIndex: 5, fontSize: 14 },
    scannerAnimation: { 
        width: '90%', height: 2, background: '#B87333', position: 'absolute', 
        top: '50%', left: '5%', boxShadow: '0 0 15px #B87333', zIndex: 10, 
        opacity: 0.6, animation: 'scan 2s infinite ease-in-out' 
    },
    controls: { marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 },
    errorBox: { background: '#351616', padding: 12, borderRadius: 12, textAlign: 'center', marginBottom: 5 },
    errorMsg: { color: '#ff8a8a', fontSize: 13, marginBottom: 8 },
    retryBtn: { background: '#4a2525', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
    fileLabel: { background: 'rgba(255,255,255,0.05)', color: '#fff', padding: '16px', borderRadius: 12, textAlign: 'center', cursor: 'pointer', fontWeight: '600', border: '1px solid rgba(255,255,255,0.1)', fontSize: 14 },
    manualBtn: { background: '#B87333', border: 'none', color: '#fff', padding: '16px', borderRadius: 12, cursor: 'pointer', fontWeight: 'bold', fontSize: 15, boxShadow: '0 4px 12px rgba(184,115,51,0.2)' }
};


